// Docs: https://www.instantdb.com/docs/modeling-data
// AgentReacher - Social scheduling with agent-driven MCP

import { i } from "@instantdb/react";

const _schema = i.schema({
	entities: {
		$files: i.entity({
			path: i.string().unique().indexed(),
			url: i.string(),
		}),
		$users: i.entity({
			email: i.string().unique().indexed().optional(),
			imageURL: i.string().optional(),
			type: i.string().optional(),
		}),
		accounts: i.entity({
			accessToken: i.string().optional(),
			accessTokenExpiresAt: i.date().optional(),
			accountId: i.string().indexed(),
			createdAt: i.date(),
			idToken: i.string().optional(),
			password: i.string().optional(),
			providerId: i.string().indexed(),
			refreshToken: i.string().optional(),
			refreshTokenExpiresAt: i.date().optional(),
			scope: i.string().optional(),
			updatedAt: i.date(),
			userId: i.string().indexed(),
		}),
		sessions: i.entity({
			createdAt: i.date(),
			expiresAt: i.date(),
			impersonatedBy: i.string().optional(),
			ipAddress: i.string().optional(),
			token: i.string().unique().indexed(),
			updatedAt: i.date(),
			userAgent: i.string().optional(),
			userId: i.string().indexed(),
		}),
		subscriptions: i.entity({
			affiliateCode: i.string().indexed().optional(),
			affiliateCommissionMonths: i.number().optional(),
			affiliateCommissionPercent: i.number().optional(),
			affiliateUserId: i.string().indexed().optional(),
			cancelAtPeriodEnd: i.boolean().optional(),
			periodEnd: i.date().optional(),
			periodStart: i.date().optional(),
			plan: i.string().indexed(),
			referenceId: i.string().indexed(),
			seats: i.number().optional(),
			status: i.string().indexed().optional(),
			stripeCustomerId: i.string().indexed().optional(),
			stripeSubscriptionId: i.string().indexed().optional(),
		}),
		workspaceCreditPeriods: i.entity({
			allowanceUnits: i.number(),
			cycleEnd: i.date().indexed(),
			cycleStart: i.date().indexed(),
			createdAt: i.date(),
			grantUnits: i.number().optional(),
			periodKey: i.string().unique().indexed(),
			reversalUnits: i.number().optional(),
			spentUnits: i.number(),
			tierSnapshot: i.string().indexed(),
			topupUnits: i.number().optional(),
			updatedAt: i.date(),
			workspaceId: i.string().indexed(),
		}),
		workspaceCreditTransactions: i.entity({
			createdAt: i.date(),
			idempotencyKey: i.string().unique().indexed().optional(),
			metadata: i.string().optional(),
			periodKey: i.string().indexed(),
			threadId: i.string().indexed().optional(),
			toolCallId: i.string().indexed().optional(),
			toolName: i.string().indexed().optional(),
			type: i.string().indexed(),
			units: i.number(),
			userId: i.string().indexed().optional(),
			workspaceId: i.string().indexed(),
		}),
		users: i.entity({
			agentLastSeenAt: i.date().optional(),
			createdAt: i.date(),
			banned: i.boolean().optional(),
			banExpires: i.date().optional(),
			banReason: i.string().optional(),
			email: i.string().unique().indexed(),
			emailVerified: i.boolean().optional(),
			image: i.string().optional(),
			name: i.string().indexed().optional(),
			onboardedAt: i.date().optional(),
			affiliateCode: i.string().unique().indexed().optional(),
			affiliateReferralCapturedAt: i.date().optional(),
			affiliateReferralCode: i.string().indexed().optional(),
			referredByUserId: i.string().indexed().optional(),
			role: i.string().optional(),
			signupMethod: i.string().indexed().optional(),
			stripeCustomerId: i.string().indexed().optional(),
			updatedAt: i.date(),
		}),
		workspaces: i.entity({
			imageUrl: i.string().optional(),
			name: i.string().indexed(),
			onboardingContentFormats: i.string().optional(),
			onboardingPriorityPlatforms: i.string().optional(),
			onboardingStatus: i.string().indexed().optional(),
			slug: i.string().unique().indexed(),
			createdAt: i.date(),
			updatedAt: i.date(),
		}),
		workspaceMembers: i.entity({
			role: i.string().indexed(),
			isActive: i.boolean().indexed(),
			joinedAt: i.date(),
		}),
		agentMcpKeys: i.entity({
			createdAt: i.date(),
			keyHash: i.string().unique().indexed(),
			keyPrefix: i.string().indexed(),
			lastUsedAt: i.date().optional(),
			name: i.string().optional(),
			ownerKey: i.string().indexed(),
			revokedAt: i.date().optional(),
			updatedAt: i.date(),
		}),
		socialChannels: i.entity({
			accessToken: i.string(),
			accountType: i.string().optional(),
			avatarUrl: i.string().optional(),
			refreshToken: i.string().optional(),
			channelKey: i.string().unique().indexed(),
			createdAt: i.date(),
			defaultPinterestBoardId: i.string().optional(),
			displayName: i.string().optional(),
			externalAccountId: i.string().indexed(),
			externalUsername: i.string().indexed().optional(),
			grantedScopes: i.string().optional(),
			lastPublishAt: i.date().optional(),
			lastVerifiedAt: i.date().optional(),
			provider: i.string().indexed(),
			status: i.string().indexed(),
			tokenExpiresAt: i.date().optional(),
			updatedAt: i.date(),
		}),
		socialPublications: i.entity({
			caption: i.string().optional(),
			channelKey: i.string().indexed(),
			createdAt: i.date(),
			errorMessage: i.string().optional(),
			mediaAssetId: i.string().indexed().optional(),
			mediaMetadata: i.string().optional(),
			mediaType: i.string().indexed(),
			ownerKey: i.string().indexed(),
			permalink: i.string().optional(),
			publishedAt: i.date().optional(),
			provider: i.string().indexed(),
			remoteContainerId: i.string().indexed().optional(),
			remoteMediaId: i.string().indexed().optional(),
			scheduledAt: i.date().optional(),
			shareToFeed: i.boolean().optional(),
			sourceUrl: i.string(),
			status: i.string().indexed(),
			updatedAt: i.date(),
		}),
		contentPosts: i.entity({
			chainOrder: i.number().optional().indexed(),
			contentBrief: i.string(),
			createdAt: i.date(),
			createdByUserId: i.string().indexed().optional(),
			errorMessage: i.string().optional(),
			generatedAssetId: i.string().indexed().optional(),
			generatedAssetUrl: i.string().optional(),
			generatedMetadata: i.string().optional(),
			generatedText: i.string().optional(),
			generationModel: i.string().optional(),
			generationProvider: i.string().optional(),
			lastScheduledByUserId: i.string().indexed().optional(),
			ownerKey: i.string().indexed(),
			parentContentPostId: i.string().optional().indexed(),
			platformOverrides: i.string().optional(),
			platforms: i.string(),
			postType: i.string().indexed(),
			prompt: i.string().optional(),
			publishedAt: i.date().optional(),
			scheduledAt: i.date().optional(),
			status: i.string().indexed(),
			updatedAt: i.date(),
		}),
		contentPostDeliveries: i.entity({
			createdAt: i.date(),
			deadLetteredAt: i.date().optional(),
			errorMessage: i.string().optional(),
			failureKind: i.string().indexed().optional(),
			failureSeverity: i.string().indexed().optional(),
			legacyPublicationId: i.string().indexed().optional(),
			lastAttemptAt: i.date().optional(),
			mediaAssetId: i.string().indexed().optional(),
			mediaType: i.string().indexed(),
			mediaUrl: i.string().optional(),
			nextAttemptAt: i.date().optional().indexed(),
			permalink: i.string().optional(),
			provider: i.string().indexed(),
			publishedAt: i.date().optional(),
			remoteContainerId: i.string().indexed().optional(),
			remoteMediaId: i.string().indexed().optional(),
			retryCount: i.number().optional(),
			scheduledAt: i.date().optional(),
			socialChannelId: i.string().indexed().optional(),
			status: i.string().indexed(),
			text: i.string().optional(),
			updatedAt: i.date(),
		}),
		mediaAssets: i.entity({
			contentType: i.string(),
			createdAt: i.date(),
			fileSizeBytes: i.number(),
			mediaType: i.string().indexed(),
			originalFilename: i.string().optional(),
			originalSourceUrl: i.string().optional(),
			ownerKey: i.string().indexed(),
			sourceKind: i.string().indexed(),
			status: i.string().indexed(),
			storageKey: i.string().unique().indexed(),
			thumbnailContentType: i.string().optional(),
			thumbnailFailureReason: i.string().optional(),
			thumbnailStatus: i.string().optional().indexed(),
			thumbnailStorageKey: i.string().optional().indexed(),
			updatedAt: i.date(),
		}),
		browserPushSubscriptions: i.entity({
			auth: i.string(),
			contentEncoding: i.string().optional(),
			createdAt: i.date(),
			endpoint: i.string(),
			endpointHash: i.string().unique().indexed(),
			p256dh: i.string(),
			revokedAt: i.date().optional(),
			status: i.string().indexed(),
			updatedAt: i.date(),
			userAgent: i.string().optional(),
			userId: i.string().indexed(),
		}),
		userNotifications: i.entity({
			actionUrl: i.string().optional(),
			body: i.string(),
			createdAt: i.date(),
			data: i.string().optional(),
			dedupeKey: i.string().unique().indexed(),
			emailError: i.string().optional(),
			emailSentAt: i.date().optional(),
			emailStatus: i.string().indexed(),
			postId: i.string().indexed().optional(),
			pushError: i.string().optional(),
			pushSentAt: i.date().optional(),
			pushStatus: i.string().indexed(),
			title: i.string(),
			type: i.string().indexed(),
			updatedAt: i.date(),
			userId: i.string().indexed(),
			workspaceId: i.string().indexed().optional(),
		}),
		verifications: i.entity({
			createdAt: i.date().indexed().optional(),
			expiresAt: i.date().indexed(),
			identifier: i.string().indexed(),
			updatedAt: i.date().optional(),
			value: i.string(),
		}),
		feedback: i.entity({
			category: i.string().indexed().optional(),
			contactEmail: i.string().optional(),
			createdAt: i.date(),
			message: i.string(),
			userEmail: i.string().optional(),
			userName: i.string().optional(),
		}),
		blogArticles: i.entity({
			contentHtml: i.string(),
			contentMarkdown: i.string().optional(),
			createdAt: i.date(),
			imageUrl: i.string().optional(),
			metaDescription: i.string().optional(),
			outrankArticleId: i.string().unique().indexed(),
			publishedAt: i.date().indexed(),
			slug: i.string().unique().indexed(),
			tags: i.string().optional(),
			title: i.string(),
			updatedAt: i.date(),
		}),
		webinarRegistrations: i.entity({
			adminNotificationStatus: i.string().indexed(),
			createdAt: i.date(),
			email: i.string().unique().indexed(),
			emailError: i.string().optional(),
			emailStatus: i.string().indexed(),
			firstName: i.string(),
			newsletterOptIn: i.boolean(),
			registeredAt: i.date().indexed(),
			updatedAt: i.date(),
		}),
		webinarChatMessages: i.entity({
			authorName: i.string(),
			createdAt: i.date().indexed(),
			message: i.string(),
			visitorId: i.string().indexed(),
			webinarSlug: i.string().indexed(),
		}),
		adminActivityLogs: i.entity({
			action: i.string().indexed(),
			actorEmail: i.string().optional(),
			actorUserId: i.string().indexed().optional(),
			createdAt: i.date(),
			payload: i.string(),
			resourceId: i.string().indexed().optional(),
			resourceType: i.string().indexed(),
			threadId: i.string().indexed().optional(),
			workspaceId: i.string().indexed().optional(),
		}),
		workspaceInvitations: i.entity({
			email: i.string().indexed(),
			token: i.string().unique().indexed(),
			role: i.string(),
			expiresAt: i.date(),
			createdAt: i.date(),
		}),
		cliAuthCodes: i.entity({
			code: i.string().unique().indexed(),
			status: i.string().indexed(),
			expiresAt: i.date(),
			createdAt: i.date(),
			apiKey: i.string().optional(),
		}),
		assistantThreads: i.entity({
			title: i.string(),
			status: i.string().indexed(),
			lastMessagePreview: i.string().optional(),
			createdAt: i.date(),
			updatedAt: i.date(),
		}),
		assistantMessages: i.entity({
			clientMessageId: i.string().indexed().optional(),
			role: i.string().indexed(),
			content: i.string().optional(),
			status: i.string().indexed().optional(),
			createdAt: i.date(),
			senderUserId: i.string().indexed().optional(),
		}),
		dataDeletionRequests: i.entity({
			completedAt: i.date().optional(),
			confirmationCode: i.string().unique().indexed(),
			externalAccountId: i.string().indexed(),
			provider: i.string().indexed(),
			requestedAt: i.date().indexed(),
			status: i.string().indexed(),
		}),
		assistantToolCalls: i.entity({
			creditCostUnits: i.number().optional(),
			name: i.string().indexed(),
			remainingCreditUnitsAfter: i.number().optional(),
			remainingCreditUnitsBefore: i.number().optional(),
			status: i.string().indexed(),
			arguments: i.string(),
			response: i.string().optional(),
			error: i.string().optional(),
			requiresConfirmation: i.boolean(),
			createdAt: i.date(),
			updatedAt: i.date(),
		}),
		listeningKeywords: i.entity({
			keyword: i.string().indexed(),
			label: i.string().optional(),
			isActive: i.boolean().indexed(),
			ownerKey: i.string().indexed(),
			lastPolledAt: i.date().optional(),
			createdAt: i.date(),
			updatedAt: i.date(),
		}),
		brandMentions: i.entity({
			dedupeKey: i.string().unique().indexed(),
			ownerKey: i.string().indexed(),
			platform: i.string().indexed(),
			externalId: i.string().indexed(),
			authorName: i.string().optional(),
			authorHandle: i.string().optional(),
			text: i.string(),
			url: i.string(),
			publishedAt: i.date().indexed(),
			createdAt: i.date().indexed(),
			isRead: i.boolean().indexed(),
		}),
	},
	links: {
		$usersLinkedPrimaryUser: {
			forward: {
				on: "$users",
				has: "one",
				label: "linkedPrimaryUser",
				onDelete: "cascade",
			},
			reverse: {
				on: "$users",
				has: "many",
				label: "linkedGuestUsers",
			},
		},
		accountsUser: {
			forward: {
				on: "accounts",
				has: "one",
				label: "user",
				onDelete: "cascade",
			},
			reverse: {
				on: "users",
				has: "many",
				label: "accounts",
			},
		},
		sessionsUser: {
			forward: {
				on: "sessions",
				has: "one",
				label: "user",
				onDelete: "cascade",
			},
			reverse: {
				on: "users",
				has: "many",
				label: "sessions",
			},
		},
		subscriptionsUser: {
			forward: {
				on: "subscriptions",
				has: "one",
				label: "user",
				onDelete: "cascade",
			},
			reverse: {
				on: "users",
				has: "many",
				label: "subscriptions",
			},
		},
		agentMcpKeysUser: {
			forward: {
				on: "agentMcpKeys",
				has: "one",
				label: "user",
				onDelete: "cascade",
			},
			reverse: {
				on: "users",
				has: "many",
				label: "agentMcpKeys",
			},
		},
		agentMcpKeysWorkspace: {
			forward: {
				on: "agentMcpKeys",
				has: "one",
				label: "workspace",
				onDelete: "cascade",
			},
			reverse: {
				on: "workspaces",
				has: "many",
				label: "agentMcpKeys",
			},
		},
		socialChannelsWorkspace: {
			forward: {
				on: "socialChannels",
				has: "one",
				label: "workspace",
				onDelete: "cascade",
			},
			reverse: {
				on: "workspaces",
				has: "many",
				label: "socialChannels",
			},
		},
		socialPublicationsChannel: {
			forward: {
				on: "socialPublications",
				has: "one",
				label: "socialChannel",
				onDelete: "cascade",
			},
			reverse: {
				on: "socialChannels",
				has: "many",
				label: "socialPublications",
			},
		},
		socialPublicationsWorkspace: {
			forward: {
				on: "socialPublications",
				has: "one",
				label: "workspace",
				onDelete: "cascade",
			},
			reverse: {
				on: "workspaces",
				has: "many",
				label: "socialPublications",
			},
		},
		contentPostsWorkspace: {
			forward: {
				on: "contentPosts",
				has: "one",
				label: "workspace",
				onDelete: "cascade",
			},
			reverse: {
				on: "workspaces",
				has: "many",
				label: "contentPosts",
			},
		},
		contentPostDeliveriesPost: {
			forward: {
				on: "contentPostDeliveries",
				has: "one",
				label: "post",
				onDelete: "cascade",
			},
			reverse: {
				on: "contentPosts",
				has: "many",
				label: "deliveries",
			},
		},
		contentPostReplies: {
			forward: {
				on: "contentPosts",
				has: "one",
				label: "parentPost",
				onDelete: "cascade",
			},
			reverse: {
				on: "contentPosts",
				has: "many",
				label: "replies",
			},
		},
		contentPostDeliveriesSocialChannel: {
			forward: {
				on: "contentPostDeliveries",
				has: "one",
				label: "socialChannel",
			},
			reverse: {
				on: "socialChannels",
				has: "many",
				label: "deliveries",
			},
		},
		mediaAssetsWorkspace: {
			forward: {
				on: "mediaAssets",
				has: "one",
				label: "workspace",
				onDelete: "cascade",
			},
			reverse: {
				on: "workspaces",
				has: "many",
				label: "mediaAssets",
			},
		},
		browserPushSubscriptionsUser: {
			forward: {
				on: "browserPushSubscriptions",
				has: "one",
				label: "user",
				onDelete: "cascade",
			},
			reverse: {
				on: "users",
				has: "many",
				label: "browserPushSubscriptions",
			},
		},
		userNotificationsUser: {
			forward: {
				on: "userNotifications",
				has: "one",
				label: "user",
				onDelete: "cascade",
			},
			reverse: {
				on: "users",
				has: "many",
				label: "notifications",
			},
		},
		userNotificationsWorkspace: {
			forward: {
				on: "userNotifications",
				has: "one",
				label: "workspace",
				onDelete: "cascade",
			},
			reverse: {
				on: "workspaces",
				has: "many",
				label: "notifications",
			},
		},
		userNotificationsContentPost: {
			forward: {
				on: "userNotifications",
				has: "one",
				label: "contentPost",
				onDelete: "cascade",
			},
			reverse: {
				on: "contentPosts",
				has: "many",
				label: "notifications",
			},
		},
		workspaceCreditPeriodsWorkspace: {
			forward: {
				on: "workspaceCreditPeriods",
				has: "one",
				label: "workspace",
				onDelete: "cascade",
			},
			reverse: {
				on: "workspaces",
				has: "many",
				label: "creditPeriods",
			},
		},
		workspaceCreditTransactionsWorkspace: {
			forward: {
				on: "workspaceCreditTransactions",
				has: "one",
				label: "workspace",
				onDelete: "cascade",
			},
			reverse: {
				on: "workspaces",
				has: "many",
				label: "creditTransactions",
			},
		},
		users$user: {
			forward: {
				on: "users",
				has: "one",
				label: "$user",
				onDelete: "cascade",
			},
			reverse: {
				on: "$users",
				has: "one",
				label: "user",
			},
		},
		workspaceMembersUser: {
			forward: {
				on: "workspaceMembers",
				has: "one",
				label: "user",
				onDelete: "cascade",
			},
			reverse: {
				on: "users",
				has: "many",
				label: "workspaceMembers",
			},
		},
		workspaceMembersWorkspace: {
			forward: {
				on: "workspaceMembers",
				has: "one",
				label: "workspace",
				onDelete: "cascade",
			},
			reverse: {
				on: "workspaces",
				has: "many",
				label: "members",
			},
		},
		feedbackUser: {
			forward: {
				on: "feedback",
				has: "one",
				label: "user",
				onDelete: "cascade",
			},
			reverse: {
				on: "users",
				has: "many",
				label: "feedback",
			},
		},
		workspaceInvitationsWorkspace: {
			forward: {
				on: "workspaceInvitations",
				has: "one",
				label: "workspace",
				onDelete: "cascade",
			},
			reverse: {
				on: "workspaces",
				has: "many",
				label: "invitations",
			},
		},
		workspaceInvitationsInviter: {
			forward: {
				on: "workspaceInvitations",
				has: "one",
				label: "inviter",
			},
			reverse: {
				on: "users",
				has: "many",
				label: "sentInvitations",
			},
		},
		cliAuthCodesUser: {
			forward: {
				on: "cliAuthCodes",
				has: "one",
				label: "user",
				onDelete: "cascade",
			},
			reverse: {
				on: "users",
				has: "many",
				label: "cliAuthCodes",
			},
		},
		cliAuthCodesWorkspace: {
			forward: {
				on: "cliAuthCodes",
				has: "one",
				label: "workspace",
				onDelete: "cascade",
			},
			reverse: {
				on: "workspaces",
				has: "many",
				label: "cliAuthCodes",
			},
		},
		assistantThreadsWorkspace: {
			forward: {
				on: "assistantThreads",
				has: "one",
				label: "workspace",
				onDelete: "cascade",
			},
			reverse: {
				on: "workspaces",
				has: "many",
				label: "assistantThreads",
			},
		},
		assistantMessagesThread: {
			forward: {
				on: "assistantMessages",
				has: "one",
				label: "thread",
				onDelete: "cascade",
			},
			reverse: {
				on: "assistantThreads",
				has: "many",
				label: "messages",
			},
		},
		assistantToolCallsThread: {
			forward: {
				on: "assistantToolCalls",
				has: "one",
				label: "thread",
				onDelete: "cascade",
			},
			reverse: {
				on: "assistantThreads",
				has: "many",
				label: "toolCalls",
			},
		},
		listeningKeywordsWorkspace: {
			forward: {
				on: "listeningKeywords",
				has: "one",
				label: "workspace",
				onDelete: "cascade",
			},
			reverse: {
				on: "workspaces",
				has: "many",
				label: "listeningKeywords",
			},
		},
		brandMentionsWorkspace: {
			forward: {
				on: "brandMentions",
				has: "one",
				label: "workspace",
				onDelete: "cascade",
			},
			reverse: {
				on: "workspaces",
				has: "many",
				label: "brandMentions",
			},
		},
		brandMentionsKeyword: {
			forward: {
				on: "brandMentions",
				has: "one",
				label: "keyword",
				onDelete: "cascade",
			},
			reverse: {
				on: "listeningKeywords",
				has: "many",
				label: "mentions",
			},
		},
	},
	rooms: {},
});

type _AppSchema = typeof _schema;
interface AppSchema extends _AppSchema {}
const schema: AppSchema = _schema;

export type { AppSchema };
export default schema;
